/**
 * iCalendar (.ics) üretimi — davet sayfasında görünen bilgilerle sınırlı (gizlilik).
 * Zamanlar UTC (Z); tüm gün etkinlikleri VALUE=DATE (UTC takvim günü).
 */
import { Timestamp } from "firebase/firestore";
import type { WeddingDoc } from "./loadWedding";
import { getVenueLatLng } from "./loadWedding";

/** iCalendar metin alanlarında ; , \ karakterlerini kaçır. */
function escapeIcsText(s: string): string {
  return s.replace(/\\/g, "\\\\").replace(/;/g, "\\;").replace(/,/g, "\\,").replace(/\r\n|\r|\n/g, "\\n");
}

/** RFC 5545: satır ≤75 karakter; uzun alanlar bölünür, devam satırı tek boşlukla başlar. */
function foldIcsProperty(name: string, value: string): string {
  const line = `${name}:${value}`;
  if (line.length <= 75) return line;
  const out: string[] = [];
  let rest = line;
  while (rest.length > 75) {
    out.push(rest.slice(0, 75));
    rest = ` ${rest.slice(75)}`;
  }
  if (rest.length > 0) out.push(rest);
  return out.join("\r\n");
}

function formatUtcDateTime(d: Date): string {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  const h = String(d.getUTCHours()).padStart(2, "0");
  const min = String(d.getUTCMinutes()).padStart(2, "0");
  const s = String(d.getUTCSeconds()).padStart(2, "0");
  return `${y}${m}${day}T${h}${min}${s}Z`;
}

function formatDateOnlyUtc(d: Date): string {
  const y = d.getUTCFullYear();
  const m = String(d.getUTCMonth() + 1).padStart(2, "0");
  const day = String(d.getUTCDate()).padStart(2, "0");
  return `${y}${m}${day}`;
}

/** Bitiş takvim günü (VALUE=DATE, DTEND dâhil değil): +1 gün. */
function addOneCalendarDayUtc(d: Date): Date {
  const x = new Date(d.getTime());
  x.setUTCDate(x.getUTCDate() + 1);
  return x;
}

/** Dosya adı: ASCII güvenli, kısa. */
export function calendarInviteIcsFilename(slug: string, eventTitle: string): string {
  const s = slug.trim() || "invite";
  const safeSlug = s.replace(/[^a-zA-Z0-9._-]+/g, "-").replace(/-+/g, "-").replace(/^-|-$/g, "") || "invite";
  const t = eventTitle
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 48);
  const base = t ? `${safeSlug}-${t}` : safeSlug;
  return `${base.slice(0, 120)}.ics`;
}

/**
 * Davet URL’si + Firestore dokümanından .ics gövdesi.
 * Tarih yoksa veya seçilmediyse `null`.
 */
/** Davet sayfasında takvim indirmesi gösterilsin mi (tarih seçili ve kayıtlı). */
export function inviteCalendarDownloadable(w: WeddingDoc): boolean {
  if (w.hasSelectedDate === false) return false;
  const startTs = w.date;
  return startTs != null && startTs instanceof Timestamp;
}

export function buildWeddingIcsString(w: WeddingDoc, invitePageUrl: string): string | null {
  if (w.hasSelectedDate === false) return null;
  const startTs = w.date;
  if (!startTs || !(startTs instanceof Timestamp)) return null;

  const start = startTs.toDate();
  const allDay = w.isAllDay === true;
  const endRaw = w.endDate;
  const endTs = endRaw instanceof Timestamp ? endRaw : null;

  const summary = (w.eventTitle ?? "").trim() || "Wedding";
  const descRaw = typeof w.description === "string" ? w.description.trim() : "";
  const name = (w.venueName ?? "").trim();
  const addr = (w.venueAddress ?? "").trim();
  const locationLine = [name, addr].filter(Boolean).join(", ") || undefined;

  const uidHost = "wedy.me";
  const uid = `wedy-${w.slug}-${formatUtcDateTime(start)}@${uidHost}`;
  const dtStamp = formatUtcDateTime(new Date());

  const lines: string[] = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Wedy.Me//Invite//EN",
    "CALSCALE:GREGORIAN",
    "METHOD:PUBLISH",
    "BEGIN:VEVENT",
    foldIcsProperty("UID", escapeIcsText(uid)),
    foldIcsProperty("DTSTAMP", dtStamp),
  ];

  if (allDay) {
    const ds = formatDateOnlyUtc(start);
    const de = formatDateOnlyUtc(addOneCalendarDayUtc(start));
    lines.push(foldIcsProperty("DTSTART;VALUE=DATE", ds));
    lines.push(foldIcsProperty("DTEND;VALUE=DATE", de));
  } else {
    lines.push(foldIcsProperty("DTSTART", formatUtcDateTime(start)));
    let end: Date;
    if (endTs) {
      end = endTs.toDate();
      if (end.getTime() <= start.getTime()) {
        end = new Date(start.getTime() + 60 * 60 * 1000);
      }
    } else {
      end = new Date(start.getTime() + 60 * 60 * 1000);
    }
    lines.push(foldIcsProperty("DTEND", formatUtcDateTime(end)));
  }

  lines.push(foldIcsProperty("SUMMARY", escapeIcsText(summary)));
  if (descRaw.length > 0) {
    lines.push(foldIcsProperty("DESCRIPTION", escapeIcsText(descRaw)));
  }
  if (locationLine) {
    lines.push(foldIcsProperty("LOCATION", escapeIcsText(locationLine)));
  }
  const geo = getVenueLatLng(w);
  if (geo) {
    lines.push(foldIcsProperty("GEO", `${geo.lat.toFixed(6)};${geo.lng.toFixed(6)}`));
  }
  lines.push(foldIcsProperty("URL", escapeIcsText(invitePageUrl.trim())));

  lines.push("END:VEVENT", "END:VCALENDAR");
  return lines.join("\r\n") + "\r\n";
}

export function downloadTextFileAsBlob(filename: string, content: string, mime: string): void {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
